import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import { CrmTaskStatus } from '@plato/data/prisma';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { ModalForm } from '@plato/ui-legacy/components/form-components/modal-form';
import { Label } from '@plato/ui-legacy/components/ui/label';
import { useDiscardChangesWarning } from '@plato/ui-legacy/lib/hooks/use-discard-changes-warning';
import { getTextContent } from '@plato/ui-legacy/lib/rich-text/get-text-content';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import { PickerValueSuggestion } from '@plato/ui/components/picker-value';
import { Dialog } from '@plato/ui/containers/dialog';
import { ArrowUpRight, CalendarIcon, CircleUserRoundIcon, PlusIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import type { CrmTaskInputDTO, CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { UsersSelect } from '../../shared/components/form-items-with-fetch/users-select.tsx';
import { createTaskFormSchema } from '../task-form-schema.ts';
import { DueDatePicker, TaskLinksInput } from './task-form-fields.tsx';

/** A ready-made task the form can be filled with in one tap. */
export type TaskFormSuggestion = {
    label: string;
    /** Plain text that becomes the description when the chip is picked. */
    description: string;
    taskIntent: CrmTaskIntent;
};

type TaskFormDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: CrmTaskInputDTO) => void | Promise<void>;
    title: string;
    initialValues?: Partial<CrmTaskInputDTO>;
    /** Offered under the description while it is empty; filling it in puts them away. */
    suggestions?: TaskFormSuggestion[];
};

export function TaskFormDialog({
    open,
    onClose,
    onSubmit,
    title,
    initialValues,
    suggestions,
}: TaskFormDialogProps): ReactNode {
    const { t } = useTranslation('tasks');
    const user = useUser();
    const { enableFileUpload } = useAppConfig();
    const { currentSessionId } = useVoiceRecording();

    const defaultValues = useMemo(() => {
        return {
            dueDate: new Date(),
            links: [],
            assignedToId: user.id,
            assignedById: user.id || '',
            createdById: user.id || '',
            status: CrmTaskStatus.OPEN,
            description: undefined,
            attachments: [],
            ...initialValues,
        };
    }, [user.id, initialValues]);

    const schema = useMemo(() => createTaskFormSchema(t('form__text_required', { ns: 'common' })), [t]);

    const form = useForm<CrmTaskInputDTO>({
        defaultValues,
        resolver: zodResolver(schema),
    });

    const {
        control,
        handleSubmit,
        setValue,
        formState: { isValid: isFormValid, isDirty, isSubmitting, errors: formErrors },
    } = form;

    const description = useWatch({ control, name: 'description' });
    const showSuggestions = useMemo(() => !getTextContent(description), [description]);

    const applySuggestion = (suggestion: TaskFormSuggestion): void => {
        // Only blank text converts to nothing, and a suggestion always carries some.
        const richText = plainTextToLexicalSync(suggestion.description);
        if (!richText) {
            return;
        }

        setValue('description', richText, { shouldDirty: true, shouldValidate: true });
        setValue('taskIntent', suggestion.taskIntent, { shouldDirty: true });
    };

    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(() => {
            onClose();
        });
    };

    const submit = handleSubmit(async (values) => {
        try {
            await onSubmit(values);
            onClose();
        } catch {
            // caller surfaces the error (e.g. toast); keep the dialog open
        }
    });

    return (
        <Dialog open={open} onClose={handleClose} disablePointerDismissal>
            <DialogForm>
                <DialogForm.Header title={title} />
                <DialogForm.Content>
                    <DialogForm.ItemList>
                        <DialogForm.Item>
                            <Label htmlFor="description" className="sr-only">
                                {t('description', { ns: 'common' })}
                            </Label>
                            <DialogForm.Body>
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field }) => (
                                        <RichTextInput
                                            trackingContext={{ source: 'task' }}
                                            placeholder={t('tasks__description_placeholder')}
                                            {...field}
                                        />
                                    )}
                                />
                            </DialogForm.Body>
                            <DialogForm.Error error={formErrors.description} />
                        </DialogForm.Item>
                        {suggestions && showSuggestions && (
                            <div className="flex flex-wrap gap-tight">
                                {suggestions.map((suggestion) => (
                                    <PickerValueSuggestion
                                        key={suggestion.label}
                                        label={suggestion.label}
                                        onClick={() => {
                                            applySuggestion(suggestion);
                                        }}
                                    >
                                        <PlusIcon />
                                    </PickerValueSuggestion>
                                ))}
                            </div>
                        )}
                    </DialogForm.ItemList>
                    <DialogForm.Group>
                        <DialogForm.ItemList subgrid>
                            <DialogForm.Item>
                                <DialogForm.Label icon={ArrowUpRight}>
                                    {t('related_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex-col">
                                    <Controller
                                        control={control}
                                        name="links"
                                        render={({ field }) => <TaskLinksInput {...field} />}
                                    />
                                </DialogForm.Body>
                                <ModalForm.Error errorMessage={formErrors.links} />
                            </DialogForm.Item>

                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="dueDate" icon={CalendarIcon}>
                                    {t('due_date', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="dueDate"
                                        render={({ field }) => <DueDatePicker {...field} />}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.dueDate} />
                            </DialogForm.Item>
                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="assignedToId" icon={CircleUserRoundIcon}>
                                    {t('assigned_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="assignedToId"
                                        render={({ field }) => (
                                            <UsersSelect {...field} className="w-full justify-start" />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.assignedToId} />
                            </DialogForm.Item>
                        </DialogForm.ItemList>
                        {enableFileUpload && (
                            <DialogForm.ItemList subgrid>
                                <Controller
                                    control={control}
                                    name="attachments"
                                    render={({ field }) => (
                                        <FileUploader
                                            {...field}
                                            value={field.value ?? []}
                                            analyticsContext="task"
                                        />
                                    )}
                                />
                            </DialogForm.ItemList>
                        )}
                    </DialogForm.Group>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    isLoading={isSubmitting}
                    onCancel={handleClose}
                    onSubmit={submit}
                    disabled={!isFormValid || !!currentSessionId}
                />
            </DialogForm>
        </Dialog>
    );
}
